import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetChapterDetailsQuery,
  useUpdateChapterMutation,
} from "../../slices/chapterApiSlice";

import { useGetSubjectsQuery } from "../../slices/subjectApiSlice";

const EditChapterScreen = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(1);
  const [isActive, setIsActive] = useState(true);

  const { data: chapter, isLoading, error } = useGetChapterDetailsQuery(id);

  const { data: subjects = [] } = useGetSubjectsQuery();

  const [updateChapter, { isLoading: loadingUpdate }] =
    useUpdateChapterMutation();

  useEffect(() => {
    if (chapter) {
      setSubject(chapter.subject?._id || "");

      setName(chapter.name || "");

      setDescription(chapter.description || "");

      setOrder(chapter.order || 1);

      setIsActive(chapter.isActive);
    }
  }, [chapter]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject || !name.trim()) {
      toast.error("Subject and chapter name are required");
      return;
    }

    try {
      await updateChapter({
        chapterId: id,

        subject,

        name: name.trim(),

        description: description.trim(),

        order: Number(order),

        isActive,
      }).unwrap();

      toast.success("Chapter updated successfully");

      navigate("/chapters");
    } catch (err) {
      const message =
        err?.data?.message || err?.error || "Failed to update chapter";

      switch (message) {
        case "Chapter not found":
          toast.error("Chapter not found");
          break;

        case "Question bank subject not found":
          toast.error("Selected subject does not exist");
          break;

        case "Chapter already exists in this subject":
          toast.error("A chapter with this name already exists");
          break;

        default:
          toast.error(message);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
        <div className="rounded-xl bg-white px-8 py-6 shadow-lg text-blue-600 font-semibold">
          Loading chapter...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
        <div className="rounded-xl bg-red-100 px-8 py-6 shadow-lg text-red-700">
          {error?.data?.message || error?.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/chapters"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Chapters
        </Link>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg border border-white/30 shadow-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800">Edit Chapter</h1>

            <p className="mt-2 text-gray-500">Update chapter information</p>
          </div>

          {loadingUpdate && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Updating chapter...
            </div>
          )}

          <form onSubmit={submitHandler} className="grid grid-cols-1 gap-6">
            {/* Subject */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Subject *
              </label>

              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select Subject</option>

                {subjects.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter Name */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Chapter Name *
              </label>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Order */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Display Order
              </label>

              <input
                type="number"
                min="1"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Status */}

            <div className="flex items-center gap-3 rounded-xl bg-slate-50 border p-4">
              <input
                id="isActive"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-5 w-5 accent-blue-600"
              />

              <label htmlFor="isActive" className="font-semibold text-gray-700">
                Active Chapter
              </label>
            </div>

            {/* Description */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Description
              </label>

              <textarea
                rows="5"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter chapter description"
                className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Buttons */}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loadingUpdate}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
              >
                {loadingUpdate ? "Updating..." : "Update Chapter"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/chapters")}
                className="rounded-xl border border-gray-300 bg-white px-6 py-4 font-semibold text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditChapterScreen;
